import classNames from 'classnames';
import React, { ComponentProps, ComponentType, memo, ReactNode } from 'react';
import { Heading } from '../../general/heading';
import { CCPAIcon } from '../../icons/icon/ccpa';
import {
  PHILO_AD_CHOICES_URL,
  PHILO_DO_NOT_SELL_URL,
  PHILO_NIELSEN_URL,
  PHILO_PRIVACY_POLICY_URL,
  PHILO_TERMS_OF_USE_URL,
  PRIVACY_LINK_TEXT,
} from '../../utils/constants';
import { IconFacebook } from '../icon/facebook';
import { IconInstagram } from '../icon/instagram';
import { IconThreads } from '../icon/threads';
import { IconTikTok } from '../icon/tiktok';
import { IconTwitter } from '../icon/twitter';
import { IconYoutube } from '../icon/youtube';
import { TextLink, TextLinkSize, TextLinkType } from '../text-link/text-link';
import { FooterAccordionColumn } from './footer-accordion-column';

import utilStyles from '../../common/utils.module.sass';
import styles from './footer.module.sass';

const ID = 'philo-footer';

const SOCIAL_LINKS = {
  Facebook: {
    icon: <IconFacebook />,
    url: 'https://www.facebook.com/PhiloTV',
  },
  Instagram: {
    icon: <IconInstagram />,
    url: 'https://www.instagram.com/philotv',
  },
  X: {
    icon: <IconTwitter />,
    url: 'https://twitter.com/philoTV',
  },
  YouTube: {
    icon: <IconYoutube />,
    url: 'https://www.youtube.com/@philotv',
  },
  TikTok: {
    icon: <IconTikTok />,
    url: 'https://www.tiktok.com/@philo_tv',
  },
  Threads: {
    icon: <IconThreads />,
    url: 'https://www.threads.net/@philotv',
  },
};

export const TERMS_LINKS = {
  Terms: PHILO_TERMS_OF_USE_URL,
  Privacy: PHILO_PRIVACY_POLICY_URL,
  'Ad Choices': PHILO_AD_CHOICES_URL,
  Accessibility: 'https://help.philo.com/using-philo/accessibility',
  'Nielsen TV Rating Measurement': PHILO_NIELSEN_URL,
} as const;

export type FooterLinkProps = Pick<
  ComponentProps<'a'>,
  'className' | 'children' | 'href' | 'target' | 'rel' | 'title'
>;

interface FooterProps {
  children?: ReactNode;
  className?: string;
  columns?: Array<Omit<ComponentProps<typeof FooterAccordionColumn>, 'headingId'>>;
  linkComponent?: 'a' | ComponentType<FooterLinkProps>;
  termsLinks?: Record<string, string>;
}

export const Footer = memo(
  ({
    children,
    linkComponent: Anchor = 'a',
    columns,
    termsLinks = TERMS_LINKS,
    className,
  }: FooterProps) => {
    const terms = Object.entries(termsLinks).map(([text, href]) => (
      <li key={text} role="listitem">
        <TextLink
          as={Anchor}
          className={styles.footerTermsLink}
          href={href}
          rel="noreferrer"
          target="_blank"
          title={text}
          type={TextLinkType.STANDALONE}
          size={TextLinkSize.SMALL}
        >
          {text}
        </TextLink>
      </li>
    ));

    const social = Object.entries(SOCIAL_LINKS).map(([label, { icon, url }]) => (
      <li key={label} role="listitem">
        <TextLink
          as={Anchor}
          className={styles.footerSocialLink}
          href={url}
          title={label}
          rel="noreferrer"
          target="_blank"
          type={TextLinkType.STANDALONE}
        >
          {icon}
        </TextLink>
      </li>
    ));

    const footerColumns = columns?.map((column, index) => (
      <FooterAccordionColumn
        {...column}
        headingId={`footer-column-title-${index}`}
        key={column.title}
      />
    ));

    return (
      <footer
        className={classNames(styles.footer, className)}
        role="contentinfo"
        aria-label="Philo Footer"
        aria-labelledby={ID}
      >
        <Heading level={2} className={utilStyles.visuallyHidden} id={ID}>
          Philo Footer
        </Heading>
        <nav role="navigation" className={styles.footerLinks} aria-label="Philo Directory">
          {footerColumns && <div className={styles.footerColumns}>{footerColumns}</div>}
          <div className={styles.footerSocialTerms}>
            <ul className={styles.footerSocial} role="list" aria-label="Philo Social Media">
              {social}
            </ul>
            <ul role="list" className={styles.footerTerms} aria-label="Terms & Privacy">
              {terms}
              <li key="ccpa" role="listitem">
                <TextLink
                  as={Anchor}
                  className={styles.footerTermsLink}
                  href={PHILO_DO_NOT_SELL_URL}
                  rel="noreferrer"
                  target="_blank"
                  type={TextLinkType.STANDALONE}
                  size={TextLinkSize.SMALL}
                >
                  {PRIVACY_LINK_TEXT} <CCPAIcon className={styles.ccpaIcon} />
                </TextLink>
              </li>
            </ul>
          </div>
        </nav>
        {children}
      </footer>
    );
  },
);

Footer.displayName = 'Footer';
